import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/lib/api';
import { subscribeNotificationSocketQuerySync } from '@/lib/notification-socket-query-sync';
import { useAuth } from './use-auth';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinProject: (projectId: string) => void;
  leaveProject: (projectId: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  /** Ref-counted project subscriptions (multiple routes / remounts can share one room). */
  const projectSubscriptionCountsRef = useRef<Map<string, number>>(new Map());
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated) {
      projectSubscriptionCountsRef.current.clear();
      const s = socketRef.current;
      if (s) {
        s.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
      return;
    }

    const token = getAccessToken();
    if (!token) return;

    const wsBase = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL || '';
    if (!wsBase) {
      // Never fall back to frontend origin for production socket connections.
      return;
    }

    const newSocket = io(`${wsBase}/notifications`, {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const unsubscribeNotificationQueries = subscribeNotificationSocketQuerySync(newSocket, queryClient);

    newSocket.on('connect', () => {
      setIsConnected(true);
      for (const [projectId, n] of projectSubscriptionCountsRef.current) {
        if (n > 0) {
          newSocket.emit('subscribe:project', { projectId });
        }
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    /** Matches NotificationsGateway.emitTaskUpdated — narrow list invalidation when listId is present. */
    newSocket.on(
      'task:updated',
      (task: { id: string; projectId: string; listId?: string | null }) => {
        const projectId = task.projectId;
        if (!projectId || !task.id) return;
        void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', task.id] });
        if (task.listId) {
          void queryClient.invalidateQueries({ queryKey: ['tasks', projectId, task.listId] });
        } else {
          void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        }
      },
    );

    // Live refresh events for access changes
    newSocket.on('access:changed', (data: { type: string; entityId: string; action: string }) => {
      // Invalidate relevant queries based on access change
      if (data.type === 'project') {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project', data.entityId] });
        queryClient.invalidateQueries({ queryKey: ['tasks', data.entityId] });
      } else if (data.type === 'list') {
        queryClient.invalidateQueries({ queryKey: ['lists'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      } else if (data.type === 'team') {
        queryClient.invalidateQueries({ queryKey: ['teams'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      }
    });

    newSocket.on('team:updated', (data: { teamId: string; action: string }) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['team', data.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-members', data.teamId] });
      // Team changes can affect project access
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    });

    newSocket.on('project:membership-changed', (data: { type: string }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (data.type === 'team') {
        queryClient.invalidateQueries({ queryKey: ['project-teams'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['project-members'] });
      }
    });

    newSocket.on('task:assignment-changed', (data: { taskId: string }) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', data.taskId] });
      let anyProject = false;
      for (const [projectId, n] of projectSubscriptionCountsRef.current) {
        if (n > 0) {
          anyProject = true;
          void queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        }
      }
      if (!anyProject) {
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
    });

    newSocket.on('refresh:required', (data: { scope: string }) => {
      // Handle broad refresh requests
      if (data.scope === 'all' || data.scope === 'projects') {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
      }
      if (data.scope === 'all' || data.scope === 'tasks') {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
      if (data.scope === 'all' || data.scope === 'teams') {
        queryClient.invalidateQueries({ queryKey: ['teams'] });
      }
      if (data.scope === 'all' || data.scope === 'lists') {
        queryClient.invalidateQueries({ queryKey: ['lists'] });
      }
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      unsubscribeNotificationQueries();
      newSocket.disconnect();
      if (socketRef.current === newSocket) {
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, queryClient]);

  const joinProject = useCallback(
    (projectId: string) => {
      if (!projectId) return;
      const m = projectSubscriptionCountsRef.current;
      const prev = m.get(projectId) ?? 0;
      m.set(projectId, prev + 1);
      if (prev === 0 && socket && isConnected) {
        socket.emit('subscribe:project', { projectId });
      }
    },
    [socket, isConnected]
  );

  const leaveProject = useCallback(
    (projectId: string) => {
      if (!projectId) return;
      const m = projectSubscriptionCountsRef.current;
      const prev = m.get(projectId) ?? 0;
      if (prev <= 0) return;
      const next = prev - 1;
      if (next === 0) {
        m.delete(projectId);
      } else {
        m.set(projectId, next);
      }
      if (prev === 1 && socket && isConnected) {
        socket.emit('unsubscribe:project', { projectId });
      }
    },
    [socket, isConnected]
  );

  return (
    <SocketContext.Provider value={{ socket, isConnected, joinProject, leaveProject }}>
      {children}
    </SocketContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with SocketProvider in this module
export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
